import { useState, useEffect } from 'react';
import {
    Box, Heading, SimpleGrid, Card, CardBody, Text,
    Icon, Table, Thead, Tbody, Tr, Th, Td, Badge,
    Stat, StatLabel, StatNumber, StatHelpText,
    Tabs, TabList, TabPanels, Tab, TabPanel,
    Button, HStack, Progress, Alert, AlertIcon, Flex,
    Menu, MenuButton, MenuList, MenuItem, useToast, Select, Center,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure, TableContainer,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Checkbox, CheckboxGroup, VStack, Divider,
    useColorModeValue
} from '@chakra-ui/react';
import { FiUsers, FiCheckCircle, FiXCircle, FiArrowLeft, FiPieChart, FiDownload, FiClipboard, FiFileText, FiRefreshCw } from 'react-icons/fi';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useNotifications } from '../context/NotificationContext';
import RollCallModal from '../components/RollCallModal';
import ExportReportModal from '../components/ExportReportModal';

const SummaryCard = ({ label, value, subtext, icon, color }) => (
    <Card borderTopWidth="4px" borderColor={`${color}.400`}>
        <CardBody display="flex" alignItems="center">
            <Box p={3} bg={`${color}.50`} borderRadius="full" mr={4}>
                <Icon as={icon} boxSize={8} color={`${color}.500`} />
            </Box>
            <Box>
                <Text color="gray.500" fontSize="sm">{label}</Text>
                <Heading size="lg" color="gray.700">{value}</Heading>
                {subtext && <Text fontSize="xs" color="gray.400">{subtext}</Text>}
            </Box>
        </CardBody>
    </Card>
);

const ClassStatsGrid = ({ classes, onClassClick }) => {
    const bg = useColorModeValue('white', 'gray.700');
    // const warningColor = useColorModeValue('red.50', 'red.900'); // Unused

    if (!classes || classes.length === 0) {
        return (
            <Center py={10}>
                <Text color="gray.500">此分類下無班級資料</Text>
            </Center>
        );
    }

    return (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4}>
            {classes.map((cls, idx) => {
                // 1. Color Logic
                let cardColor = 'red.400';
                const isFull = cls.rate === 100 || (cls.total > 0 && cls.total === cls.arrived + cls.leave);

                if (isFull) {
                    cardColor = 'green.400';
                } else if (cls.department === 'English' || cls.department === '美語部') {
                    cardColor = 'purple.400';
                }

                // 2. Prepare Missing/Leave Lists (Logic moved out of JSX)
                const notArrivedList = cls.missing_list
                    ? cls.missing_list.filter(m => m.status === 'NOT_ARRIVED' || !m.status)
                    : (cls.missing || []).filter(m => m.status === 'NOT_ARRIVED');
                const leaveList = cls.leave_list
                    ? cls.leave_list
                    : (cls.missing || []).filter(m => m.status === 'LEAVE');

                return (
                    <Card
                        key={idx}
                        bg={bg}
                        shadow="sm"
                        borderTop="4px solid"
                        borderColor={cardColor}
                        _hover={{ shadow: 'md', transform: 'translateY(-2px)', cursor: 'pointer' }}
                        transition="all 0.2s"
                        onClick={() => onClassClick && onClassClick(cls)}
                    >
                        <CardBody>
                            <HStack justifyContent="space-between" mb={3}>
                                <Heading size="md" color="gray.700">{cls.name}</Heading>
                                <Badge colorScheme={cls.rate === 100 ? 'green' : cls.rate < 80 ? 'red' : 'yellow'}>
                                    到課率 {cls.rate}%
                                </Badge>
                            </HStack>
                            <Text mb={2} fontSize="sm" color="gray.500">
                                {cls.department === 'English' ? '英文部' : '全科班'} | 應到: {cls.total} / 實到: {cls.arrived}
                                <Text as="span" ml={3} color="orange.500" fontWeight="bold">請假: {cls.leave || 0}</Text>
                            </Text>
                            <Progress value={cls.rate} colorScheme={cls.rate === 100 ? 'green' : 'blue'} borderRadius="full" mb={4} />

                            {/* Render Lists */}
                            {notArrivedList.length > 0 && (
                                <Box bg="red.50" p={3} borderRadius="md" mb={2}>
                                    <Text fontSize="xs" fontWeight="bold" color="red.600" mb={1}>未到班:</Text>
                                    <HStack flexWrap="wrap" spacing={2}>
                                        {notArrivedList.map(m => (
                                            <Badge key={m.id} variant="solid" colorScheme="red" size="sm">
                                                {m.full_name}
                                            </Badge>
                                        ))}
                                    </HStack>
                                </Box>
                            )}

                            {leaveList.length > 0 && (
                                <Box p={3} borderRadius="md" border="1px solid" borderColor="green.200" bg="green.50" mb={2}>
                                    <Text fontSize="xs" fontWeight="bold" color="green.600" mb={1}>請假:</Text>
                                    <HStack flexWrap="wrap" spacing={2}>
                                        {leaveList.map(m => (
                                            <Badge key={m.id} variant="outline" colorScheme="green" size="sm">
                                                {m.full_name}
                                            </Badge>
                                        ))}
                                    </HStack>
                                </Box>
                            )}

                            {notArrivedList.length === 0 && leaveList.length === 0 && (
                                <Alert status="success" borderRadius="md" py={2}>
                                    <AlertIcon />
                                    全員到齊
                                </Alert>
                            )}
                        </CardBody>
                    </Card>
                );
            })}
        </SimpleGrid >
    );
};

const DailyAttendancePage = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        arrived: 0,
        notArrived: 0,
        rate: 0
    });

    // Group by Class
    const [classStats, setClassStats] = useState([]);

    // Track Active Tab for Context-Aware Export
    const [tabIndex, setTabIndex] = useState(0);

    const navigate = useNavigate();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const exportDisclosure = useDisclosure();
    const singleExportDisclosure = useDisclosure(); // 班級匯出（當日，可多選）
    const [exportClassIds, setExportClassIds] = useState([]); // 勾選的班級 id（字串陣列）
    const cancelRef = useRef();
    const { lastDataUpdate } = useNotifications();

    // Roll Call Modal State
    const {
        isOpen: isRollCallOpen,
        onOpen: onRollCallOpen,
        onClose: onRollCallClose
    } = useDisclosure();
    const [selectedClassForRollCall, setSelectedClassForRollCall] = useState(null);

    const handleClassClick = (cls) => {
        console.log("Class Clicked:", cls); // Debug: Check if ID exists
        if (cls.id) {
            setSelectedClassForRollCall(cls);
            onRollCallOpen();
        } else {
            console.error("Missing Class ID for:", cls);
            toast({
                title: "無法開啟點名",
                description: "班級資料缺少 ID，請聯繫管理員。",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    // Reset Logic
    const handleResetStatus = async () => {
        try {
            setLoading(true);
            await authAPI.triggerDailyReset();
            toast({
                title: "重置成功",
                description: "所有學生狀態已更新為 [未到班]",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
            onClose();
            fetchData(); // Refresh data
        } catch (error) {
            toast({
                title: "重置失敗",
                description: error.response?.data?.detail || "無法執行重置操作",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Export Logic
    const handleCopyToClipboard = () => {
        const dateStr = new Date().toLocaleDateString();
        let titleSuffix = "";
        let targetClasses = [];

        // 1. Context-Aware Filtering
        if (tabIndex === 0) {
            titleSuffix = "";
            targetClasses = classStats;
        } else if (tabIndex === 1) {
            titleSuffix = " - 全科班";
            targetClasses = classStats.filter(c => c.department === 'AfterSchool' || !c.department);
        } else if (tabIndex === 2) {
            titleSuffix = " - 英文部";
            targetClasses = classStats.filter(c => c.department === 'English');
        } else if (tabIndex === 3) {
            titleSuffix = " - 才藝班";
            // Check for both identifiers just in case
            targetClasses = classStats.filter(c => c.department === 'Talent' || c.department === '才藝班' || c.department === '才藝部');
        } else if (tabIndex === 4) {
            // Tab 4: Missing List Only (Shifted from 3 to 4)
            titleSuffix = " - 缺席名單";
            targetClasses = []; // Special handling below
        }

        let text = `【每日點名匯總${titleSuffix}】 ${dateStr}\n\n`;

        if (tabIndex === 4) {
            // Special Export for Missing List Tab
            text += `[全校未到人員名單]\n`;
            text += `總未到人數: ${stats.notArrived}\n\n`;

            // Collect all missing from all classes to ensure we have the latest status
            // OR use the 'students' state which holds missingStudents from fetchData?
            // In fetchData: setStudents(missingStudents); so 'students' IS the missing list.
            students.forEach(s => {
                const parents = s.parents || [];
                const parentInfo = parents.map(p => `${p.full_name} (${p.phone_number})`).join(' / ');
                text += `- ${s.full_name} (${s.class?.name || '無班級'}) | 家長: ${parentInfo || '無'}\n`;
            });

        } else {
            // Standard Class Export (Filtered by Department)

            // Calculate Stats for this Export Context
            let contextTotal = 0;
            let contextArrived = 0;
            let contextNotArrived = 0; // Derived from Total - Arrived

            targetClasses.forEach(cls => {
                contextTotal += cls.total;
                contextArrived += cls.arrived;

                text += `[${cls.name}] 應到:${cls.total} / 實到:${cls.arrived}`;
                if (cls.missing.length > 0) {
                    text += `\n未到名單:\n`;
                    cls.missing.forEach(s => {
                        text += `- ${s.full_name}\n`;
                    });
                } else {
                    text += ` (全到)\n`;
                }
                text += `\n`;
            });

            // Footer Statistics
            contextNotArrived = contextTotal - contextArrived;

            text += `----------------\n`;
            text += `總應到: ${contextTotal}\n`;
            text += `總實到: ${contextArrived}\n`;
            text += `總未到: ${contextNotArrived}\n`;
        }

        navigator.clipboard.writeText(text).then(() => {
            toast({
                title: "已複製到剪貼簿",
                description: `已匯出${titleSuffix || "全部"}資料`,
                status: "success",
                duration: 2000,
                isClosable: true,
            });
        });
    };

    const handleDownloadCSV = (overrideClasses = null) => {
        const csvRows = [];
        const dateStr = new Date().toLocaleDateString();

        // 1. Determine Scope based on Tab（或勾選的多個班級：只匯出選定班級當日，格式同一般 CSV）
        let targetClasses = [];
        let titleSuffix = "";
        const isCustom = Array.isArray(overrideClasses) && overrideClasses.length > 0;

        if (isCustom) {
            targetClasses = overrideClasses;
            titleSuffix = overrideClasses.length === 1 ? `(${overrideClasses[0].name})` : `(選定${overrideClasses.length}班)`;
        } else if (tabIndex === 0) {
            targetClasses = classStats;
        } else if (tabIndex === 1) {
            titleSuffix = "(安親部)";
            targetClasses = classStats.filter(c => c.department === 'AfterSchool' || !c.department);
        } else if (tabIndex === 2) {
            targetClasses = classStats.filter(c => c.department === 'English');
        } else if (tabIndex === 3) {
            titleSuffix = "(才藝班)";
            targetClasses = classStats.filter(c => c.department === 'Talent' || c.department === '才藝班' || c.department === '才藝部');
        }

        // Special handling for Tab 4 (Missing List) — 勾選班級匯出時走一般班級格式
        if (!isCustom && tabIndex === 4) {
            csvRows.push([`【全校未到人員名單】 ${dateStr}`]);
            csvRows.push(['班級', '學生姓名', '家長姓名', '家長電話', '狀態']);

            // Use the global 'students' state which contains the missing list
            const sortedStudents = [...students].sort((a, b) => {
                const clsA = a.class?.name || '';
                const clsB = b.class?.name || '';
                if (clsA !== clsB) return clsA.localeCompare(clsB);
                return a.full_name.localeCompare(b.full_name);
            });

            sortedStudents.forEach(s => {
                const parents = s.parents || [];
                const parentName = parents.map(p => p.full_name).join(' / ');
                const parentPhone = parents.map(p => p.phone_number).join(' / ');
                const statusText = s.status === 'LEAVE' ? '請假' : '未到';

                csvRows.push([
                    s.class?.name || '未分班',
                    s.full_name,
                    parentName,
                    parentPhone,
                    statusText
                ]);
            });

        } else {
            // Tabs 0, 1, 2: Class Stats Summary + Detailed List

            // Calculate Context Stats
            let contextTotal = 0;
            let contextArrived = 0;
            let contextLeave = 0;

            targetClasses.forEach(cls => {
                contextTotal += cls.total;
                contextArrived += cls.arrived;
                contextLeave += cls.leave || 0;
            });
            const contextNotArrived = contextTotal - contextArrived; // This counts both Missing and Leave as 'Not Arrived' generally, or we can separate? 
            // In the UI cards: Total (Correct), Arrived, Not Arrived (Red box). 
            // Let's match the summary row logic:

            csvRows.push([`【今日點名概況${titleSuffix}】 ${dateStr}`]);
            csvRows.push(['班級', '應到', '實到', '請假', '未到', '到課率']);

            targetClasses.forEach(cls => {
                // Individual Class Rows
                const clsNotArrived = cls.total - cls.arrived;
                // Note: 'cls.total - cls.arrived' includes Leaves if Total covers everyone.
                // Should we subtract Leave from Not Arrived column? 
                // UI shows: Total | Arrived | Not Arrived (Red). The Not Arrived RED box usually means (Total - Arrived).
                // Let's stick to simple math: NotArrived = Total - Arrived.

                csvRows.push([
                    cls.name,
                    cls.total,
                    cls.arrived,
                    cls.leave,
                    clsNotArrived,
                    `${cls.rate}%`
                ]);
            });

            // Context Total Summary
            const contextRate = contextTotal > 0 ? Math.round((contextArrived / contextTotal) * 100) : 0;
            csvRows.push([]);
            csvRows.push(['總計', contextTotal, contextArrived, contextLeave, contextNotArrived, `${contextRate}%`]);
            csvRows.push([]);
            csvRows.push([]);

            // Detailed List (From Target Classes)
            csvRows.push([`【未進班詳細名單${titleSuffix}】`]);
            csvRows.push(['班級', '學生姓名', '家長姓名', '家長電話', '狀態']);

            // Collect all missing students from the filtered classes
            let contextMissingStudents = [];
            targetClasses.forEach(cls => {
                if (cls.missing && cls.missing.length > 0) {
                    // cls.missing contains both LEAVE and NOT_ARRIVED
                    // Backend now provides 'parents' array in these items
                    contextMissingStudents = [...contextMissingStudents, ...cls.missing.map(s => ({ ...s, className: cls.name }))];
                }
            });

            // Sort
            contextMissingStudents.sort((a, b) => {
                if (a.className !== b.className) return a.className.localeCompare(b.className);
                return a.full_name.localeCompare(b.full_name);
            });

            contextMissingStudents.forEach(s => {
                const parents = s.parents || [];
                const parentName = parents.map(p => p.name || p.full_name).join(' / '); // Backend sends 'name', frontend adapter mapped to 'full_name'. Use 'name' if raw from backend.
                // Wait, 'cls.missing' in frontend state comes from 'updatedClassStats' in fetchData.
                // In fetchData, we map: const mergedList = [...backendMissing, ...backendLeave];
                // And backend sends 'parents': [{name, phone, relation}]
                // The frontend adapter inside handleDownloadCSV isn't applied here if we use raw 'classStats'.
                // 'classStats' state is set via 'updatedClassStats'. 
                // Let's check fetchData 'updatedClassStats' mapping logic.
                // Line 356: 'const updatedClassStats = summary.class_stats.map...'
                // It just merges lists. It DOES NOT adapt structure.
                // So s.parents will be [{name:..., phone:..., relation:...}] (Backend format).

                const parentPhone = parents.map(p => p.phone || p.phone_number).join(' / ');
                const statusText = s.status === 'LEAVE' ? '請假' : '未到';

                csvRows.push([
                    s.className || '未分班',
                    s.full_name,
                    parentName,
                    parentPhone,
                    statusText
                ]);
            });
        }

        // Add BOM for Excel Chinese support
        const bom = '\uFEFF';
        const csvContent = bom + csvRows.map(e => e.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `點名報表${titleSuffix}_${new Date().toLocaleDateString().replace(/\//g, '')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        fetchData();
    }, [lastDataUpdate]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const summaryRes = await authAPI.getDailySummary();
            const summary = summaryRes.data;

            // Use Global Lists provided by Backend (Schedule-Aware)
            // Adapter: Map backend structure to frontend expectation if needed
            // Backend structure: { id, full_name, class_name, parents: [{name, phone, relation}], status }
            // Frontend structure: { id, full_name, class: {name}, parents: [{phone_number}], status }
            const adaptStudent = (s) => ({
                id: s.id,
                full_name: s.full_name,
                status: s.status,
                class: { name: s.class_name },
                parents: (s.parents || []).map(p => ({
                    full_name: p.name || p.full_name,
                    phone_number: p.phone || p.phone_number,
                    role: p.relation
                }))
            });

            // Fallback Logic: If summary has total but NO class_stats (or empty), manual fetch
            // But checking 'total > 0' and 'class_stats empty' might be valid if no classes today (unlikely for 236 students).
            // Let's assume if class_stats is empty but total > 0, we need fallback.
            let statsData = {
                class_stats: summary.class_stats || [],
                global_missing: summary.global_missing_list || [],
                global_leave: summary.global_leave_list || [],
                total: summary.total || 0,
                arrived: summary.arrived || 0,
                leave: summary.leave || 0,
                not_arrived: summary.not_arrived || 0,
                rate: summary.rate || 0
            };

            if ((!summary.class_stats || summary.class_stats.length === 0) && (summary.total > 0)) {
                console.warn("Daily Summary missing class_stats, attempting fallback fetch...");
                try {
                    // [Step 2] Fetch All Students (Increase limit to avoid pagination issues)
                    // Default limit might be 50 or 100, causing missing students in class counts.
                    const [classesRes, studentsRes] = await Promise.all([
                        authAPI.getClasses({ limit: 100 }), // Fetch all classes
                        authAPI.getStudents({ limit: 1000 }) // Fetch all students
                    ]);

                    const allClasses = classesRes.data;
                    const allStudents = studentsRes.data;

                    // [Step 1] Prepare Date/Time Variables for Filtering
                    // [Step 1] Prepare Date/Time Variables for Filtering (Strict Taipei Time UTC+8)
                    // Fix: Do not rely on client local time (new Date()). Use Intl to force Taipei context.
                    const now = new Date();
                    const taipeiFormatter = new Intl.DateTimeFormat('en-CA', {
                        timeZone: 'Asia/Taipei',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour12: false
                    });

                    // Format: YYYY-MM-DD
                    const todayStr = taipeiFormatter.format(now);
                    const [yyyy, mm, dd] = todayStr.split('-').map(Number);

                    // Calculate Weekday based on Taipei Date (Avoid local TZ day shift)
                    const taipeiDateObj = new Date(Date.UTC(yyyy, mm - 1, dd));
                    const jsDay = taipeiDateObj.getUTCDay(); // 0=Sun

                    // Weekday Mapping: JS(0=Sun) -> DB(0=Mon)
                    const dbDay = (jsDay + 6) % 7;

                    // [Step 2] Fetch Details for ALL Classes
                    // Previously optimization: Only English. Now: All must check schedule.
                    const fetchedDetails = await Promise.all(allClasses.map(async (c) => {
                        try {
                            const res = await authAPI.getClass(c.id);
                            // Merge list data to preserve fields
                            return { ...c, ...res.data };
                        } catch (e) {
                            console.warn(`Failed to fetch details for class ${c.id}`, e);
                            return c;
                        }
                    }));

                    // [Step 3] Filter ALL Classes by Schedule/Exception
                    // Goal: "No schedule = No show"
                    const activeClasses = fetchedDetails.filter(cls => {
                        // 1. Check Exceptions (Highest Priority)
                        if (cls.exceptions && Array.isArray(cls.exceptions)) {
                            const exception = cls.exceptions.find(ex => ex.date === todayStr);
                            if (exception) {
                                if (exception.type === 'SKIP') return false;
                                if (exception.type === 'ADD') return true;
                            }
                        }

                        // 2. Check Schedules
                        if (cls.schedules && Array.isArray(cls.schedules) && cls.schedules.length > 0) {
                            // Relaxed check (==)
                            return cls.schedules.some(s => s.day_of_week == dbDay);
                        }

                        // 3. Default: If no schedule found, Inactive (Hidden)
                        return false;
                    });

                    // [Step 4] Fetch Isolated Attendance Records (Per Class)
                    // This ensures that status in AnChin Class doesn't affect English Class.
                    const attendancePromises = activeClasses.map(c =>
                        authAPI.attendance.getClassAttendance(c.id, todayStr)
                            .then(res => ({ classId: c.id, records: res.data }))
                            .catch(() => ({ classId: c.id, records: [] }))
                    );

                    const attendanceResults = await Promise.all(attendancePromises);
                    const classAttendanceMap = {}; // { classId: { studentId: status } }
                    attendanceResults.forEach(item => {
                        if (item.records) {
                            const recMap = {};
                            item.records.forEach(r => recMap[r.student_id] = r.status);
                            classAttendanceMap[item.classId] = recMap;
                        }
                    });

                    // Manual Calculation (Class Cards)
                    const manualClassStats = activeClasses.map(cls => {
                        const clsRecords = classAttendanceMap[cls.id] || {};

                        // Find students for this class
                        const clsStudents = allStudents.filter(s => {
                            // 1. Primary Class Match
                            if (s.class && s.class.id && cls.id && String(s.class.id) === String(cls.id)) return true;
                            if (s.class_id && cls.id && String(s.class_id) === String(cls.id)) return true;

                            // 2. Multi-Class Match
                            if (s.classes && Array.isArray(s.classes)) {
                                return s.classes.some(sc => {
                                    const targetId = String(cls.id);
                                    if (sc.id && String(sc.id) === targetId) return true;
                                    if (sc.class_id && String(sc.class_id) === targetId) return true;
                                    return (sc.name || sc.class_name) === cls.name;
                                });
                            }
                            return false;
                        });

                        // Determine Isolated Status
                        const statsStudents = clsStudents.map(s => {
                            const localStatus = clsRecords[s.id];
                            // Priority: 1. Local Record 2. Global LEAVE 3. NOT_ARRIVED
                            if (localStatus) return { ...s, status: localStatus };
                            if (s.status === 'LEAVE') return { ...s, status: 'LEAVE' };
                            return { ...s, status: 'NOT_ARRIVED' };
                        });

                        const total = statsStudents.length;
                        const arrived = statsStudents.filter(s => ['ARRIVED', 'PRESENT', 'HOMEWORK_PENDING', 'READY_FOR_PICKUP', 'PARENT_EN_ROUTE', 'PICKUP_COMPLETED', 'IN_CLASS'].includes(s.status)).length;
                        const leave = statsStudents.filter(s => s.status === 'LEAVE').length;
                        const notArrived = total - arrived - leave;

                        return {
                            id: cls.id,
                            name: cls.name,
                            department: cls.department,
                            schedules: cls.schedules,
                            total,
                            arrived,
                            leave,
                            not_arrived: notArrived,
                            rate: total > 0 ? Math.round(((arrived + leave) / total) * 100) : 0,
                            missing: statsStudents.filter(s => !s.status || s.status === 'NOT_ARRIVED'),
                            missing_list: statsStudents.filter(s => !s.status || s.status === 'NOT_ARRIVED'),
                            leave_list: statsStudents.filter(s => s.status === 'LEAVE')
                        };
                    }).filter(c => c.total > 0);

                    // Update statsData
                    statsData.class_stats = manualClassStats;

                    // [Fix] Recalculate Global Stats with UNIQUE Students
                    // Multi-class students appear in multiple class cards, causing double counting in totals.
                    // We must filter allStudents against activeClasses, then count unique IDs.

                    // 1. Identify all active class IDs
                    const activeClassIds = new Set(activeClasses.map(c => c.id));

                    // 2. Filter students who belong to AT LEAST ONE active class
                    const uniqueActiveStudents = allStudents.filter(s => {
                        // Check Primary
                        if (s.class?.id && activeClassIds.has(s.class.id)) return true;
                        if (s.class_id && activeClassIds.has(s.class_id)) return true;
                        // Check Multi
                        if (s.classes && Array.isArray(s.classes)) {
                            return s.classes.some(c => activeClassIds.has(c.id));
                        }
                        return false;
                    });

                    // 3. Calc Global Stats from this unique set
                    const gTotal = uniqueActiveStudents.length;
                    const gArrived = uniqueActiveStudents.filter(s => ['ARRIVED', 'HOMEWORK_PENDING', 'READY_FOR_PICKUP', 'PARENT_EN_ROUTE', 'PICKUP_COMPLETED', 'IN_CLASS'].includes(s.status)).length;
                    const gLeave = uniqueActiveStudents.filter(s => s.status === 'LEAVE').length;

                    statsData.total = gTotal;
                    statsData.arrived = gArrived;
                    statsData.leave = gLeave;
                    statsData.not_arrived = gTotal - gArrived - gLeave;
                    if (statsData.not_arrived < 0) statsData.not_arrived = 0;

                    statsData.rate = statsData.total > 0 ? Math.round((statsData.arrived / statsData.total) * 100) : 0;

                    // Global Missing List (Unique)
                    // These are students from the unique set who are NOT arrived and NOT leave (strictly missing?)
                    // Or follow standard statuses.
                    statsData.global_missing = uniqueActiveStudents.filter(s => s.status === 'NOT_ARRIVED' || !s.status);
                    statsData.global_leave = uniqueActiveStudents.filter(s => s.status === 'LEAVE');

                } catch (fallbackErr) {
                    console.error("Fallback fetch failed", fallbackErr);
                }
            }

            const missingList = (statsData.global_missing || []).map(adaptStudent);
            const leaveList = (statsData.global_leave || []).map(adaptStudent);
            const allMissingStudents = [...missingList, ...leaveList];

            // Update Missing List State (for CSV or Display)
            setStudents(allMissingStudents);

            setStats({
                total: statsData.total,
                arrived: statsData.arrived,
                leave: statsData.leave,
                notArrived: statsData.not_arrived,
                rate: statsData.rate
            });

            // Map missing student objects from Backend Lists directly
            const updatedClassStats = (statsData.class_stats || []).map(clsStat => {
                // Ensure items from missing_list have status='NOT_ARRIVED'
                const backendMissing = (clsStat.missing_list || []).map(s => ({
                    ...adaptStudent(s), // Adapt format first
                    status: 'NOT_ARRIVED'
                }));

                // Ensure items from leave_list have status='LEAVE'
                const backendLeave = (clsStat.leave_list || []).map(s => ({
                    ...adaptStudent(s), // Adapt format first
                    status: 'LEAVE'
                }));

                // Merge for the 'missing' prop which the Card expects
                const mergedList = [...backendMissing, ...backendLeave];

                return {
                    ...clsStat,
                    // Normalize class name (API might return class_name or name)
                    name: clsStat.class_name || clsStat.name || '未命名班級',
                    missing: mergedList
                };
            });

            setClassStats(updatedClassStats);
        } catch (error) {
            console.error(error);
            toast({
                title: "載入失敗",
                description: "無法取得點名數據",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };



    return (
        <Flex direction="column" h="100%" overflow="hidden" py={4}>
            {/* Header Section - Fixed */}
            <Box flexShrink={0}>


                <Flex justifyContent="space-between" alignItems="center" mb={6}>
                    <HStack spacing={4} alignItems="center">
                        <Box p={2} bg="blue.500" borderRadius="md" color="white">
                            <Icon as={FiPieChart} boxSize={6} />
                        </Box>
                        <Heading size="lg" color="gray.800">每日點名狀況</Heading>
                    </HStack>

                    <Menu>
                        <MenuButton as={Button} rightIcon={<FiDownload />} colorScheme="blue" variant="solid">
                            匯出名單
                        </MenuButton>
                        <MenuList>
                            <MenuItem icon={<FiClipboard />} onClick={handleCopyToClipboard}>
                                複製到剪貼簿 (Line)
                            </MenuItem>
                            <MenuItem icon={<FiFileText />} onClick={() => handleDownloadCSV()}>
                                下載 Excel (CSV)
                            </MenuItem>
                            <MenuItem icon={<FiFileText />} onClick={() => { setExportClassIds([]); singleExportDisclosure.onOpen(); }}>
                                下載班級 (當日 CSV，可多選)
                            </MenuItem>
                            <MenuItem icon={<FiFileText />} onClick={exportDisclosure.onOpen}>
                                下載 Excel (進階報表)
                            </MenuItem>
                            <MenuItem icon={<FiRefreshCw />} onClick={onOpen} color="red.500">
                                重置所有狀態 (未到班)
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Flex>


            </Box>

            {/* Export Modal */}
            <ExportReportModal
                isOpen={exportDisclosure.isOpen}
                onClose={exportDisclosure.onClose}
            />

            {/* 班級匯出（當日，可多選）Modal */}
            <Modal isOpen={singleExportDisclosure.isOpen} onClose={singleExportDisclosure.onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>下載班級點名（當日，可多選）</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text mb={2} fontSize="sm" color="gray.600">
                            勾選要匯出的班級（可多選；格式與「下載 Excel (CSV)」相同）：
                        </Text>
                        <Checkbox
                            isChecked={classStats.length > 0 && exportClassIds.length === classStats.length}
                            isIndeterminate={exportClassIds.length > 0 && exportClassIds.length < classStats.length}
                            onChange={(e) => setExportClassIds(e.target.checked ? classStats.map((c) => String(c.id)) : [])}
                            fontWeight="bold"
                        >
                            全選 / 全不選
                        </Checkbox>
                        <Divider my={2} />
                        <CheckboxGroup value={exportClassIds} onChange={(vals) => setExportClassIds(vals)}>
                            <VStack align="stretch" spacing={2} maxH="320px" overflowY="auto">
                                {classStats.map((cls) => (
                                    <Checkbox key={cls.id} value={String(cls.id)}>
                                        {cls.name}（應到 {cls.total} / 實到 {cls.arrived}）
                                    </Checkbox>
                                ))}
                            </VStack>
                        </CheckboxGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={singleExportDisclosure.onClose}>取消</Button>
                        <Button
                            colorScheme="blue"
                            leftIcon={<FiDownload />}
                            isDisabled={exportClassIds.length === 0}
                            onClick={() => {
                                const chosen = classStats.filter((c) => exportClassIds.includes(String(c.id)));
                                if (chosen.length) {
                                    handleDownloadCSV(chosen);
                                    singleExportDisclosure.onClose();
                                }
                            }}
                        >
                            下載 CSV{exportClassIds.length ? `（${exportClassIds.length}班）` : ''}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Alert Dialog for Reset */}
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            重置所有學生狀態
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            您確定要將 **所有** 學生的狀態重置為 [未到班] 嗎？
                            <br />
                            此操作通常在每日凌晨執行，手動執行將會覆蓋當前所有的點名紀錄。
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                取消
                            </Button>
                            <Button colorScheme="red" onClick={handleResetStatus} ml={3} isLoading={loading}>
                                確定重置
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Roll Call Modal */}
            <RollCallModal
                isOpen={isRollCallOpen}
                onClose={onRollCallClose}
                classData={selectedClassForRollCall}
                onUpdate={fetchData}
            />

            {/* Scrollable Content Section */}
            <Box flex="1" overflowY="auto" pb={4} pr={1} css={{
                '&::-webkit-scrollbar': { width: '8px' },
                '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
            }}>
                {/* Summary Cards (Always Visible) */}
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
                    <SummaryCard
                        label="應到人數 (總數)"
                        value={stats.total}
                        icon={FiUsers}
                        color="gray"
                    />
                    <SummaryCard
                        label="實到人數"
                        value={stats.arrived}
                        icon={FiCheckCircle}
                        color="green"
                    />
                    <SummaryCard
                        label="未到人數"
                        value={stats.notArrived}
                        icon={FiXCircle}
                        color="red"
                    />
                    <Card bg="blue.50" borderTopWidth="4px" borderColor="blue.400">
                        <CardBody>
                            <Heading size="md" mb={2} color="blue.700">今日出勤率</Heading>
                            <Heading size="2xl" color="blue.600">{stats.rate}%</Heading>
                            <Progress value={stats.rate} colorScheme="blue" size="sm" mt={4} borderRadius="full" />
                        </CardBody>
                    </Card>
                </SimpleGrid>

                <Tabs variant="enclosed" colorScheme="blue" isLazy onChange={(index) => setTabIndex(index)}>
                    <TabList mb={4}>
                        <Tab fontWeight="bold">全部班級</Tab>
                        <Tab fontWeight="bold">全科班</Tab>
                        <Tab _selected={{ color: 'blue.500', borderColor: 'blue.500', bg: 'blue.50' }}>英文班 (English)</Tab>
                        <Tab _selected={{ color: 'green.500', borderColor: 'green.500', bg: 'green.50' }}>才藝班 (Talent)</Tab>
                        <Tab _selected={{ color: 'red.500', borderColor: 'red.500', bg: 'red.50' }}>缺席名單 ({stats.notArrived})</Tab>
                    </TabList>
                    <TabPanels>
                        {/* Tab 0: All Classes */}
                        <TabPanel p={0} pt={4}>
                            <ClassStatsGrid classes={classStats.filter(c => true)} onClassClick={handleClassClick} />
                        </TabPanel>

                        {/* Tab 1: AfterSchool */}
                        <TabPanel p={0} pt={4}>
                            <ClassStatsGrid classes={classStats.filter(c => c.department === 'AfterSchool' || !c.department)} onClassClick={handleClassClick} />
                        </TabPanel>

                        {/* Tab 2: English */}
                        <TabPanel p={0} pt={4}>
                            <ClassStatsGrid classes={classStats.filter(c => c.department === 'English')} onClassClick={handleClassClick} />
                        </TabPanel>

                        {/* Tab 3: Talent Classes (New) */}
                        <TabPanel p={0} pt={4}>
                            {loading ? (
                                <Text>載入中...</Text>
                            ) : (
                                (() => {
                                    const talentClasses = classStats.filter(c => c.department === 'Talent' || c.department === '才藝班' || c.department === '才藝部');
                                    if (talentClasses.length === 0) {
                                        return (
                                            // 卡片面 white：行政外殼就是 neutral.50（=gray.50），
                                            // 原本的填色與底色同階，虛線框裡外看起來一樣
                                            <Center py={10} bg="white" borderRadius="md" border="1px dashed" borderColor="gray.200">
                                                <Text color="gray.400" fontSize="lg">今日無才藝班課程</Text>
                                            </Center>
                                        );
                                    }
                                    return <ClassStatsGrid classes={talentClasses} onClassClick={handleClassClick} />;
                                })()
                            )}
                        </TabPanel>

                        {/* Tab 4: Missing List (Shifted) */}
                        <TabPanel p={0} pt={4}>
                            <Card>
                                <CardBody>
                                    <TableContainer overflowX="auto" w="100%" maxW={{ base: 'calc(100vw - 64px)', md: '100%' }}>
                                        <Table variant="simple" minW="600px" whiteSpace="nowrap">
                                            <Thead bg="gray.50">
                                                <Tr>
                                                    <Th>姓名</Th>
                                                    <Th>班級</Th>
                                                    <Th>家長聯繫</Th>
                                                    <Th>狀態</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {students
                                                    .filter(s => s.status === 'NOT_ARRIVED')
                                                    .map(s => (
                                                        <Tr key={s.id}>
                                                            <Td fontWeight="bold">{s.full_name}</Td>
                                                            <Td>{s.class?.name || '無'}</Td>
                                                            <Td color="gray.500">
                                                                {s.parents?.length > 0
                                                                    ? s.parents.map(p => p.full_name || p.phone_number).join(', ')
                                                                    : '無綁定家長'}
                                                            </Td>
                                                            <Td>
                                                                <Badge colorScheme="red">未到班</Badge>
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                {stats.notArrived === 0 && (
                                                    <Tr>
                                                        <Td colSpan={4} textAlign="center" py={10} color="green.500">
                                                            <Icon as={FiCheckCircle} boxSize={6} mb={2} />
                                                            <Text>今日全員到齊！</Text>
                                                        </Td>
                                                    </Tr>
                                                )}
                                            </Tbody>
                                        </Table>
                                    </TableContainer>
                                </CardBody>
                            </Card>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>
        </Flex>
    );
};

export default DailyAttendancePage;
